import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useDeleteAttachment } from 'hooks/api/useAttachments';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type DeleteAttachmentParamsT = {
  ticketId: string;
  fileName: string;
  originalName: string;
};

type UseDeleteAttachmentPopupT = {
  isDeletingAttachment: boolean;
  handleDeleteAttachment: (params: DeleteAttachmentParamsT, onClose: NoopT) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useDeleteAttachmentPopup = (): UseDeleteAttachmentPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const deleteAttachmentMutation = useDeleteAttachment({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );
  const handleDeleteAttachment = useCallback(
    (params: DeleteAttachmentParamsT, onClose: NoopT) => () => {
      deleteAttachmentMutation.mutate(params, {
        onSuccess: () => {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: 'Attachment successfully deleted',
              snackBarSeverity: 'success',
            })
          );
          onClose();
        },
        onError: error => {
          const errorMsg =
            getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
              snackBarSeverity: 'error',
            })
          );
          onClose();
        },
      });
    },
    [deleteAttachmentMutation, dispatch]
  );

  return {
    isDeletingAttachment: deleteAttachmentMutation.isLoading,
    handleDeleteAttachment,
    handleClosePopup,
  };
};

export default useDeleteAttachmentPopup;
